import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Affix,
  Button,
  DatePicker,
  Divider,
  Input,
  Radio,
  Spin,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";
import moment from "moment";
import { Loading, Report } from "notiflix";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";

import "../create/styles.css";

import { requestToken } from "../../../auth/AuthServices";
import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";
import { normalizeLevel } from "../utils";
import { NON_STAFF_NAMES } from "../../../constants/pa.constant";

const { TextArea } = Input;

const generateLocalId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;

const slugifyLabel = (label = "") =>
  (label || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const EDIT_DRAFT_KEY_PREFIX = "pa-edit-draft";

const buildEditDraftKey = (recordId, employeeId) =>
  `${EDIT_DRAFT_KEY_PREFIX}:${recordId || "unknown"}:${employeeId || "anonymous"}`;

const MAX_EVIDENCE_SIZE = 10 * 1024 * 1024;
const I_LOVE_PDF_URL = "https://www.ilovepdf.com";

const formatFileSize = (size) => {
  if (!size) return "";
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / 1024).toFixed(1)} KB`;
};

const buildPerformanceCommentMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item?.aspect_code) {
      map[item.aspect_code] = item.comment || "";
    }
  });
  return map;
};

const buildCompetencyCommentMap = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item?.competency_code && item.statement_no !== undefined) {
      const normalizedCode =
        item.competency_code.includes("_") && !item.competency_code.endsWith("_")
          ? item.competency_code
          : slugifyLabel(item.competency_label) || item.competency_code;
      if (normalizedCode) {
        const key = `${normalizedCode}_${item.statement_no}`;
        map[key] = item.comment || "";
      }
    }
  });
  return map;
};

const buildTaskCommentLookup = (items = []) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item.order_no !== undefined) {
      const key = `${item.year || ""}-${item.order_no}`;
      map[key] = item.comment || "";
    }
  });
  return map;
};

const mapApprovalProcess = (source) => {
  if (!source) return null;
  if (source.data) return source;
  return { data: source };
};

const buildPerformanceOriginal = (performances = []) => {
  const map = {};
  (performances || []).forEach((item) => {
    if (item?.aspect_code) {
      map[item.aspect_code] = item.selected_value ?? null;
    }
  });
  return map;
};

const buildCompetencyOriginal = (competencies = []) => {
  const map = {};
  (competencies || []).forEach((item) => {
    if (item?.competency_code && item.statement_no !== undefined) {
      const key = `${item.competency_code}_${item.statement_no}`;
      map[key] = item.score ?? null;
    }
  });
  return map;
};

const buildTaskOriginal = (tasks = []) => {
  const map = {};
  (tasks || []).forEach((task, index) => {
    const key = task.order_no || index + 1;
    map[key] = {
      year: task.year,
      description: task.description || "",
      kpi_indicator: task.kpi_indicator || "",
      achievement_date: task.achievement_date
        ? moment(task.achievement_date).toISOString()
        : null,
      result_comment: task.result_comment || "",
      note: task.note || "",
      evidence_file_url: task.evidence_file_url || "",
      evidence_file_name: task.evidence_file_name || "",
    };
  });
  return map;
};

const detectChangedPerformances = (master = [], currentMap = {}, originalMap = {}) =>
  master
    .filter((aspect) => {
      const current = currentMap[aspect.aspect_code];
      const original = originalMap[aspect.aspect_code];
      return typeof current === "number" && current !== original;
    })
    .map((aspect) => aspect.aspect_code);

const detectChangedCompetencies = (master = [], currentMap = {}, originalMap = {}) => {
  const changed = [];
  master.forEach((comp) => {
    comp.statements.forEach((stmt) => {
      const key = `${comp.competency_code}_${stmt.statement_no}`;
      const current = currentMap[key];
      const original = originalMap[key];
      if (current && current !== original) {
        changed.push(key);
      }
    });
  });
  return changed;
};

const normalizeTaskForCompare = (task, orderNo) => ({
  order_no: orderNo,
  year: task.year,
  description: task.description || "",
  kpi_indicator: task.kpi_indicator || "",
  achievement_date: task.achievement_date
    ? moment(task.achievement_date).toISOString()
    : null,
  result_comment: task.result_comment || "",
  note: task.note || "",
  evidence_file_url: task.evidence_file_url || "",
  evidence_file_name: task.evidence_file_name || "",
});

const detectChangedTasks = (tasks = [], originalMap = {}) => {
  const changed = [];
  tasks.forEach((task, idx) => {
    const orderNo = task.order_no || idx + 1;
    const current = normalizeTaskForCompare(task, orderNo);
    const original = originalMap[orderNo] || {};
    const diff =
      original.year !== current.year ||
      original.description !== current.description ||
      original.kpi_indicator !== current.kpi_indicator ||
      (original.achievement_date || null) !== (current.achievement_date || null) ||
      original.result_comment !== current.result_comment ||
      original.note !== current.note ||
      original.evidence_file_url !== current.evidence_file_url ||
      original.evidence_file_name !== current.evidence_file_name;
    if (diff) {
      changed.push({
        order_no: orderNo,
        approver_comment: task.approver_comment || "",
      });
    }
  });
  return changed;
};

const buildBlankTask = (year) => ({
  localId: generateLocalId(),
  description: "",
  kpi_indicator: "",
  achievement_date: null,
  result_comment: "",
  note: "",
  approver_comment: "",
  year,
  evidence_file_name: "",
  evidence_file_url: "",
  evidence_file_key: "",
  evidence_file_size: null,
  evidence_uploaded_at: null,
});

const ensureMinimumTasks = (tasks, min, year) => {
  const list = [...tasks];
  while (list.length < min) {
    list.push(buildBlankTask(year));
  }
  return list;
};

const serializeTaskForDraft = (task = {}) => ({
  ...task,
  achievement_date: task.achievement_date
    ? moment(task.achievement_date).toISOString()
    : null,
});

const serializeTaskListForDraft = (tasks = []) =>
  tasks.map((task) => serializeTaskForDraft(task));

const hydrateDraftTaskList = (tasks = [], defaultYear) => {
  if (!Array.isArray(tasks) || !tasks.length) return null;
  const normalized = tasks.map((task) => {
    const mapped = {
      ...buildBlankTask(defaultYear),
      ...task,
    };
    mapped.year = task.year || defaultYear;
    mapped.localId = task.localId || generateLocalId();
    mapped.achievement_date = task.achievement_date
      ? moment(task.achievement_date)
      : null;
    return mapped;
  });

  return ensureMinimumTasks(normalized, 3, defaultYear);
};

const serializeTasks = (tasks = []) =>
  tasks.map((task, index) => ({
    order_no: index + 1,
    year: task.year,
    description: task.description,
    kpi_indicator: task.kpi_indicator,
    achievement_date: task.achievement_date
      ? moment(task.achievement_date).toISOString()
      : null,
    result_comment: task.result_comment,
    note: task.note,
    evidence_file_name: task.evidence_file_name || "",
    evidence_file_url: task.evidence_file_url || "",
    evidence_file_key: task.evidence_file_key || "",
    evidence_file_size: task.evidence_file_size || null,
    evidence_uploaded_at: task.evidence_uploaded_at || null,
  }));

const buildPerformanceSelection = (performances = []) => {
  const map = {};
  performances.forEach((item) => {
    map[item.aspect_code] = item.selected_value ?? null;
  });
  return map;
};

const buildCompetencySelection = (competencies = []) => {
  const map = {};
  competencies.forEach((item) => {
    const key = `${item.competency_code}_${item.statement_no}`;
    map[key] = item.score;
  });
  return map;
};

const isPerformanceChanged = (aspectCode, currentMap, originalMap) => {
  const current = currentMap[aspectCode];
  const original = originalMap[aspectCode];
  return typeof current === "number" && current !== original;
};

const isCompetencyChanged = (key, currentMap, originalMap) => {
  const current = currentMap[key];
  const original = originalMap[key];
  return current && current !== original;
};

const isTaskChanged = (task, originalMap, index) => {
  const orderNo = task.order_no || index + 1;
  const current = normalizeTaskForCompare(task, orderNo);
  const original = originalMap[orderNo] || {};
  return (
    original.year !== current.year ||
    original.description !== current.description ||
    original.kpi_indicator !== current.kpi_indicator ||
    (original.achievement_date || null) !== (current.achievement_date || null) ||
    original.result_comment !== current.result_comment ||
    original.note !== current.note ||
    original.evidence_file_url !== current.evidence_file_url ||
    original.evidence_file_name !== current.evidence_file_name
  );
};

const mapTasksFromDetail = (tasks = [], year, commentLookup = {}, commentCache = {}) =>
  tasks
    .filter((task) => Number(task.year) === Number(year))
    .map((task, index) => {
      const resolvedYear = task.year || year;
      const orderNo = task.order_no || index + 1;
      const commentKey = `${resolvedYear}-${orderNo}`;
      const approverComment = (commentLookup[commentKey] || "").toString();
      const mappedTask = {
        localId: task._id || generateLocalId(),
        order_no: orderNo,
        description: task.description || "",
        kpi_indicator: task.kpi_indicator || "",
        achievement_date: task.achievement_date
          ? moment(task.achievement_date)
          : null,
        result_comment: task.result_comment || "",
        note: task.note || "",
        approver_comment: approverComment,
        year: resolvedYear,
        evidence_file_name: task.evidence_file_name || "",
        evidence_file_url: task.evidence_file_url || "",
        evidence_file_key: task.evidence_file_key || "",
        evidence_file_size: task.evidence_file_size || null,
        evidence_uploaded_at: task.evidence_uploaded_at || null,
      };
      if (mappedTask.localId) {
        commentCache[mappedTask.localId] = approverComment;
      }
      return mappedTask;
    });

function PATransactionEdit() {
  const { recordId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [performanceMaster, setPerformanceMaster] = useState([]);
  const [competencyMaster, setCompetencyMaster] = useState([]);
  const [performanceValues, setPerformanceValues] = useState({});
  const [competencyValues, setCompetencyValues] = useState({});
  const [pastTasks, setPastTasks] = useState([]);
  const [futureTasks, setFutureTasks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [performanceComments, setPerformanceComments] = useState({});
  const [competencyComments, setCompetencyComments] = useState({});
  const [evidenceLoading, setEvidenceLoading] = useState({});
  const [draftHydrated, setDraftHydrated] = useState(false);
  const performanceCommentRef = useRef({});
  const competencyCommentRef = useRef({});
  const taskCommentRef = useRef({});
  const originalPerformanceRef = useRef({});
  const originalCompetencyRef = useRef({});
  const originalTasksRef = useRef({});

  const periodYear = detail?.period_year || new Date().getFullYear();
  const planningYear = periodYear + 1;
  const performanceLabelMap = useMemo(() => {
    const map = {};
    performanceMaster.forEach((aspect) => {
      map[aspect.aspect_code] = aspect.aspect_label;
    });
    return map;
  }, [performanceMaster]);
  const competencyLabelMap = useMemo(() => {
    const map = {};
    competencyMaster.forEach((comp) => {
      comp.statements.forEach((statement) => {
        const codeKey = `${comp.competency_code}_${statement.statement_no}`;
        map[codeKey] = comp.competency_label;
        const labelKey = `${slugifyLabel(comp.competency_label)}_${statement.statement_no}`;
        map[labelKey] = comp.competency_label;
      });
    });
    return map;
  }, [competencyMaster]);
  const sessionInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("session_farms") || "{}");
    } catch (error) {
      return {};
    }
  }, []);
  const draftStorageKey = useMemo(
    () => buildEditDraftKey(recordId, sessionInfo?.employee_id),
    [recordId, sessionInfo?.employee_id]
  );

  const levelForMaster = useMemo(
      () => normalizeLevel(detail?.level || ""),
    [detail?.level]
  );
  
  const isNonStaff = NON_STAFF_NAMES.includes(levelForMaster);

  useEffect(() => {
    setDraftHydrated(false);
  }, [recordId]);
  const approvalData = useMemo(() => {
    if (!detail) return null;
    const source = detail.approval_process_id || detail.approval_process;
    if (source?.data) return source.data;
    return source || null;
  }, [detail]);
  const canComment = useMemo(() => {
    if (!approvalData || !sessionInfo?.employee_id) return false;
    if (approvalData.status === "Reject") return false;
    const detailList = approvalData.detail || [];
    const isDirectApprover = detailList.some(
      (item) =>
        (item.approver?.employee_id || item.approver_id) ===
        sessionInfo.employee_id
    );
    const isDeputyApprover = detailList.some(
      (item) => item.deputy_approver === sessionInfo.employee_id
    );
    const formSetting = approvalData.form_id?.form_setting?.[0] || {};
    const isPIC =
      Array.isArray(formSetting.pic) &&
      formSetting.pic.includes(sessionInfo.employee_id) &&
      !!formSetting.pic_approval_refer &&
      detailList.some(
        (item) =>
          (item.approver?.nik || item.approver_id) ===
          formSetting.pic_approval_refer
      );
    return isDirectApprover || isDeputyApprover || isPIC;
  }, [approvalData, sessionInfo?.employee_id]);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setLoadingDetail(true);
        const token = await requestToken();
        const response = await API.GetPATransaction(
          HeaderFarms(token),
          recordId
        );
        const rawRecord = response?.data || response;
        const normalizedRecord = { ...rawRecord };
        const approvalSource =
          normalizedRecord.approval_process_id ||
          normalizedRecord.approval_process;
        normalizedRecord.approval_process_id =
          mapApprovalProcess(approvalSource);
        setDetail(normalizedRecord);

        const commentSource =
          normalizedRecord.approver_adjustments?.comments || {};
        const perfCommentMap = buildPerformanceCommentMap(
          commentSource.performances
        );
        const compCommentMap = buildCompetencyCommentMap(
          commentSource.competencies
        );
        const taskCommentLookup = buildTaskCommentLookup(
          commentSource.tasks
        );
        performanceCommentRef.current = { ...perfCommentMap };
        competencyCommentRef.current = { ...compCommentMap };
        taskCommentRef.current = {};
        setPerformanceComments(perfCommentMap);
        setCompetencyComments(compCommentMap);

        const params = {
          year: normalizedRecord.period_year || new Date().getFullYear(),
          level: normalizeLevel(normalizedRecord.level),
        };

        const [perfResp, compResp] = await Promise.all([
          API.ListPAMasterPerformance(HeaderFarms(token), params),
          API.ListPAMasterCompetencies(HeaderFarms(token), params),
        ]);

        const perfMaster = perfResp.data?.[0]?.aspects || [];
        const compMaster = compResp.data?.[0]?.competencies || [];

        setPerformanceMaster(perfMaster);
        setCompetencyMaster(compMaster);

        const perfValueMap = buildPerformanceSelection(
          normalizedRecord.performances
        );
        perfMaster.forEach((aspect) => {
          if (typeof perfValueMap[aspect.aspect_code] !== "number") {
            perfValueMap[aspect.aspect_code] = null;
          }
        });

        setPerformanceValues(perfValueMap);
        setCompetencyValues(
          buildCompetencySelection(normalizedRecord.competencies)
        );
        originalPerformanceRef.current = buildPerformanceOriginal(
          normalizedRecord.performances
        );
        originalCompetencyRef.current = buildCompetencyOriginal(
          normalizedRecord.competencies
        );
        originalTasksRef.current = buildTaskOriginal(normalizedRecord.tasks);

        const taskCommentCache = {};
        const currentTasks = mapTasksFromDetail(
          normalizedRecord.tasks,
          params.year,
          taskCommentLookup,
          taskCommentCache
        );
        const futureTasksData = mapTasksFromDetail(
          normalizedRecord.tasks,
          params.year + 1,
          taskCommentLookup,
          taskCommentCache
        );
        taskCommentRef.current = { ...taskCommentCache };
        setPastTasks(ensureMinimumTasks(currentTasks, 3, params.year));
        setFutureTasks(
          ensureMinimumTasks(futureTasksData, 3, params.year + 1)
        );
      } catch (error) {
        console.error("loadDetail error:", error);
        Report.warning(
          "Data tidak ditemukan",
          "Record Performance Appraisal tidak tersedia.",
          "Kembali",
          () => navigate(-1)
        );
      } finally {
        setLoadingDetail(false);
        setLoadingMaster(false);
      }
    };

    loadDetail();
  }, [recordId, navigate]);

  useEffect(() => {
    if (
      !detail ||
      loadingDetail ||
      loadingMaster ||
      draftHydrated ||
      !draftStorageKey
    ) {
      if (!detail && !loadingDetail && !loadingMaster && !draftHydrated) {
        setDraftHydrated(true);
      }
      return;
    }

    const rawDraft = localStorage.getItem(draftStorageKey);
    if (!rawDraft) {
      setDraftHydrated(true);
      return;
    }

    try {
      const saved = JSON.parse(rawDraft);
      if (saved?.record_id !== detail.id_record) {
        localStorage.removeItem(draftStorageKey);
        setDraftHydrated(true);
        return;
      }

      if (saved.performanceValues) {
        const next = {};
        let hasData = false;
        performanceMaster.forEach((aspect) => {
          const value = saved.performanceValues[aspect.aspect_code];
          if (typeof value === "number") {
            next[aspect.aspect_code] = value;
            hasData = true;
          }
        });
        if (hasData) {
          setPerformanceValues((prev) => ({ ...prev, ...next }));
        }
      }

      if (saved.competencyValues) {
        const next = {};
        let hasData = false;
        competencyMaster.forEach((comp) => {
          comp.statements.forEach((stmt) => {
            const key = `${comp.competency_code}_${stmt.statement_no}`;
            const value = saved.competencyValues[key];
            if ([1, 2, 3].includes(value)) {
              next[key] = value;
              hasData = true;
            }
          });
        });
        if (hasData) {
          setCompetencyValues((prev) => ({ ...prev, ...next }));
        }
      }

      if (saved.performanceComments) {
        setPerformanceComments((prev) => ({
          ...prev,
          ...saved.performanceComments,
        }));
      }

      if (saved.competencyComments) {
        setCompetencyComments((prev) => ({
          ...prev,
          ...saved.competencyComments,
        }));
      }

      const nextPast = hydrateDraftTaskList(saved.pastTasks, periodYear);
      if (nextPast) {
        setPastTasks(nextPast);
      }

      const nextFuture = hydrateDraftTaskList(saved.futureTasks, planningYear);
      if (nextFuture) {
        setFutureTasks(nextFuture);
      }
    } catch (error) {
      console.error("hydrate edit draft error:", error);
      localStorage.removeItem(draftStorageKey);
    } finally {
      setDraftHydrated(true);
    }
  }, [
    detail,
    loadingDetail,
    loadingMaster,
    draftHydrated,
    draftStorageKey,
    performanceMaster,
    competencyMaster,
    periodYear,
    planningYear,
  ]);

  useEffect(() => {
    if (
      !draftStorageKey ||
      !detail ||
      loadingDetail ||
      loadingMaster ||
      !draftHydrated
    ) {
      return;
    }

    const payload = {
      record_id: detail.id_record,
      employee_id: sessionInfo?.employee_id || null,
      saved_at: new Date().toISOString(),
      performanceValues,
      competencyValues,
      performanceComments,
      competencyComments,
      pastTasks: serializeTaskListForDraft(pastTasks),
      futureTasks: serializeTaskListForDraft(futureTasks),
    };

    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(payload));
    } catch (error) {
      console.error("save edit draft error:", error);
    }
  }, [
    performanceValues,
    competencyValues,
    performanceComments,
    competencyComments,
    pastTasks,
    futureTasks,
    detail,
    loadingDetail,
    loadingMaster,
    draftHydrated,
    draftStorageKey,
    sessionInfo?.employee_id,
  ]);

  const handlePerformanceChange = (aspectCode, value) => {
    setPerformanceValues((prev) => ({
      ...prev,
      [aspectCode]: value,
    }));
  };

  const handleCompetencyChange = (key, value) => {
    setCompetencyValues((prev) => ({ ...prev, [key]: value }));
  };

  const handlePerformanceCommentChange = (aspectCode, value) => {
    setPerformanceComments((prev) => ({ ...prev, [aspectCode]: value }));
  };

  const handleCompetencyCommentChange = (key, value) => {
    setCompetencyComments((prev) => ({ ...prev, [key]: value }));
  };

  const updateTaskValue = (type, index, field, value) => {
    const setter = type === "past" ? setPastTasks : setFutureTasks;
    const list = type === "past" ? pastTasks : futureTasks;
    const next = [...list];
    if (field && typeof field === "object") {
      next[index] = { ...next[index], ...field };
    } else {
      next[index] = { ...next[index], [field]: value };
    }
    setter(next);
  };

  const addTaskRow = (type) => {
    const setter = type === "past" ? setPastTasks : setFutureTasks;
    const year = type === "past" ? periodYear : planningYear;
    setter((prev) => [...prev, buildBlankTask(year)]);
  };

  const removeTaskRow = (type, index) => {
    const setter = type === "past" ? setPastTasks : setFutureTasks;
    const list = type === "past" ? pastTasks : futureTasks;
    if (list.length <= 3) return;
    const removed = list[index];
    setter(list.filter((_, idx) => idx !== index));
    if (removed?.localId && taskCommentRef.current[removed.localId] !== undefined) {
      const nextRef = { ...taskCommentRef.current };
      delete nextRef[removed.localId];
      taskCommentRef.current = nextRef;
    }
  };

  const buildEvidenceKey = (type, index) => `${type}-${index}`;

  const handleEvidenceUpload = async (type, index, file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      Report.warning(
        "Format tidak didukung",
        "Unggah file berformat PDF.",
        "Mengerti"
      );
      return;
    }

    if (file.size > MAX_EVIDENCE_SIZE) {
      Report.warning(
        "File terlalu besar",
        "Ukuran evidence maksimal 10MB.",
        "Mengerti"
      );
      return;
    }

    try {
      const key = buildEvidenceKey(type, index);
      setEvidenceLoading((prev) => ({ ...prev, [key]: true }));
      const token = await requestToken();
      const headers = {
        ...HeaderFarms(token),
      };
      const formData = new FormData();
      formData.append("file", file);
      formData.append("year", type === "past" ? periodYear : planningYear);

      const response = await API.UploadPATaskEvidence(headers, formData);
      const meta = response.data || {};
      updateTaskValue(type, index, {
        evidence_file_name: meta.file_name || file.name,
        evidence_file_url: meta.file_url || "",
        evidence_file_key: meta.file_key || "",
        evidence_file_size: meta.file_size || file.size,
        evidence_uploaded_at: meta.uploaded_at || new Date().toISOString(),
      });
    } catch (error) {
      console.error("UploadPATaskEvidence error:", error);
      Report.warning(
        "Upload gagal",
        error?.response?.data?.message ||
          "Tidak dapat mengunggah evidence. Coba lagi.",
        "Mengerti"
      );
    } finally {
      setEvidenceLoading((prev) => {
        const key = buildEvidenceKey(type, index);
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleEvidenceRemove = (type, index) => {
    updateTaskValue(type, index, {
      evidence_file_name: "",
      evidence_file_url: "",
      evidence_file_key: "",
      evidence_file_size: null,
      evidence_uploaded_at: null,
    });
  };

  const validateForm = () => {
    const missingPerformance = performanceMaster.some((aspect) => {
      const value = performanceValues[aspect.aspect_code];
      return typeof value !== "number";
    });

    if (missingPerformance) {
      Report.warning(
        "Validasi Data",
        "Mohon pilih nilai untuk seluruh aspek kinerja.",
        "Mengerti"
      );
      return false;
    }

    const missingCompetency = competencyMaster.some((comp) =>
      comp.statements.some((stmt) => {
        const key = `${comp.competency_code}_${stmt.statement_no}`;
        return !competencyValues[key];
      })
    );

    if (missingCompetency) {
      Report.warning(
        "Validasi Data",
        "Mohon isi seluruh penilaian kompetensi.",
        "Mengerti"
      );
      return false;
    }

    const invalidPast = pastTasks.some(
      (task) =>
        !task.description ||
        !task.kpi_indicator ||
        !task.result_comment ||
        !task.achievement_date
    );
    if (!isNonStaff && invalidPast) {
      Report.warning(
        "Validasi Data",
        "Lengkapi seluruh kolom pada tugas 12 bulan terakhir.",
        "Mengerti"
      );
      return false;
    }

    const invalidFuture = futureTasks.some(
      (task) => !task.description || !task.kpi_indicator
    );
    
    if (!isNonStaff && invalidFuture) {
      Report.warning(
        "Validasi Data",
        "Lengkapi seluruh kolom pada tugas 12 bulan berikutnya.",
        "Mengerti"
      );
      return false;
    }

    return true;
  };

  const buildPerformancePayload = () => {
    const payload = [];

    performanceMaster.forEach((aspect) => {
      const selectedValue = performanceValues[aspect.aspect_code];
      if (typeof selectedValue !== "number") return;
      payload.push({
        aspect_code: aspect.aspect_code,
        aspect_label: aspect.aspect_label,
        order: aspect.order,
        selected_value: selectedValue,
      });
    });

    return payload;
  };

  const buildCompetencyPayload = () => {
    const payload = [];
    competencyMaster.forEach((comp) => {
      comp.statements.forEach((stmt) => {
        const key = `${comp.competency_code}_${stmt.statement_no}`;
        payload.push({
          competency_code: comp.competency_code,
          statement_no: stmt.statement_no,
          level: normalizeLevel(detail.level),
          score: competencyValues[key],
        });
      });
    });
    return payload;
  };

  const buildTaskCommentPayload = () => {
    const entries = [];
    const collect = (tasks, yearOffset = 0) => {
      tasks.forEach((task, index) => {
        const value = task.approver_comment;
        if (value === undefined) return;
        const trimmed = (value || "").trim();
        const previous = (taskCommentRef.current[task.localId] || "").trim();
        if (!trimmed && !previous) return;
        if (trimmed === previous) return;
        entries.push({
          year: task.year,
          order_no: index + 1,
          comment: value || "",
        });
      });
    };
    collect(pastTasks);
    collect(futureTasks);
    return entries;
  };

  const buildApproverCommentPayload = () => {
    const payload = {};

    if (Object.keys(performanceComments).length) {
      const perfEntries = [];
      Object.entries(performanceComments).forEach(([aspectCode, value]) => {
        if (value === undefined) return;
        const trimmed = (value || "").trim();
        const previous = (performanceCommentRef.current[aspectCode] || "").trim();
        if (!trimmed && !previous) return;
        if (trimmed === previous) return;
        perfEntries.push({
          aspect_code: aspectCode,
          aspect_label: performanceLabelMap[aspectCode] || "",
          comment: value || "",
        });
      });
      if (perfEntries.length) {
        payload.performances = perfEntries;
      }
    }

    if (Object.keys(competencyComments).length) {
      const compEntries = [];
      Object.entries(competencyComments).forEach(([key, value]) => {
        if (value === undefined) return;
        const trimmed = (value || "").trim();
        const previous = (competencyCommentRef.current[key] || "").trim();
        if (!trimmed && !previous) return;
        if (trimmed === previous) return;
        const separatorIndex = key.lastIndexOf("_");
        if (separatorIndex <= 0) return;
        const competencyCode = key.slice(0, separatorIndex);
        const statementNo = key.slice(separatorIndex + 1);
        compEntries.push({
          competency_code: competencyCode,
          competency_label: competencyLabelMap[key] || "",
          statement_no: Number(statementNo),
          comment: value || "",
        });
      });
      if (compEntries.length) {
        payload.competencies = compEntries;
      }
    }

    const taskEntries = buildTaskCommentPayload();
    if (taskEntries.length) {
      payload.tasks = taskEntries;
    }

    return Object.keys(payload).length ? payload : null;
  };

  const handleSubmit = async () => {
    if (!detail) return;
    if (!validateForm()) return;

    if (canComment) {
      const changedPerf = detectChangedPerformances(
        performanceMaster,
        performanceValues,
        originalPerformanceRef.current
      );

      const changedComp = detectChangedCompetencies(
        competencyMaster,
        competencyValues,
        originalCompetencyRef.current
      );

      const changedTasks = detectChangedTasks(
        [...pastTasks, ...futureTasks],
        originalTasksRef.current
      );

      const missingPerfComment = changedPerf.some(
        (code) => !(performanceComments[code] || "").trim()
      );
      const missingCompComment = changedComp.some(
        (key) => !(competencyComments[key] || "").trim()
      );
      const missingTaskComment = changedTasks.some(
        (task) => !(task.approver_comment || "").trim()
      );

      if (missingPerfComment || missingCompComment || missingTaskComment) {
        Report.warning(
          "Validasi Komentar",
          "Berikan komentar atas setiap nilai yang diubah oleh atasan.",
          "Mengerti"
        );
        return;
      }
    }

    try {
      setSubmitting(true);
      Loading.dots("Menyimpan perubahan...");
      const token = await requestToken();

      const payload = {
        fullname: detail.fullname,
        position: detail.position,
        level: detail.level,
        department: detail.department,
        branch: detail.branch,
        period_year: periodYear,
        period_start: detail.period_start,
        period_end: detail.period_end,
        performances: buildPerformancePayload(),
        competencies: buildCompetencyPayload(),
        tasks: [
          ...serializeTasks(pastTasks),
          ...serializeTasks(futureTasks),
        ],
      };

      const approverCommentPayload = buildApproverCommentPayload();
      if (approverCommentPayload) {
        payload.approver_comments = approverCommentPayload;
      }

      await API.UpdatePATransaction(
        HeaderFarms(token),
        recordId,
        payload
      );

      if (draftStorageKey) {
        localStorage.removeItem(draftStorageKey);
      }

      Report.success(
        "Perubahan disimpan",
        "Penilaian berhasil diperbarui.",
        "Lihat Detail",
        () => navigate(`/pa/view/${recordId}`)
      );
    } catch (error) {
      console.error("UpdatePATransaction error:", error);
      Report.warning(
        "Oops.. ada kendala",
        error?.response?.data?.error?.[0]?.name ||
          "Gagal menyimpan perubahan.",
        "Mengerti"
      );
    } finally {
      setSubmitting(false);
      Loading.remove();
    }
  };

  const renderTaskSection = (title, tasks, type) => (
    <div className="pa-card">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <p className="pa-section-title mb-1">{title}</p>
          <small className="text-muted">
            Minimal 3 tugas. Isi seluruh kolom yang tersedia.
          </small>
        </div>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => addTaskRow(type)}
        >
          Tambah Tugas
        </Button>
      </div>
      {tasks.map((task, index) => (
        <div
          key={task.localId || `${type}-${index}`}
          className="pa-task-card"
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="mb-0 fw-bold">Tugas #{index + 1}</p>
            {tasks.length > 3 && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeTaskRow(type, index)}
              >
                Hapus
              </Button>
            )}
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="pa-field-label">Deskripsi Tugas</label>
              <TextArea
                rows={3}
                value={task.description}
                onChange={(e) =>
                  updateTaskValue(type, index, "description", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <label className="pa-field-label">Indikator / KPI</label>
              <TextArea
                rows={3}
                value={task.kpi_indicator}
                onChange={(e) =>
                  updateTaskValue(type, index, "kpi_indicator", e.target.value)
                }
              />
            </div>
            <div className="col-md-4">
              <label className="pa-field-label">Tanggal Pencapaian</label>
              <DatePicker
                style={{ width: "100%" }}
                format="DD MMM YYYY"
                value={
                  task.achievement_date ? moment(task.achievement_date) : null
                }
                onChange={(date) =>
                  updateTaskValue(type, index, "achievement_date", date)
                }
              />
            </div>
            <div className="col-md-4">
              <label className="pa-field-label">
                {type === "past" ? "Hasil / Komentar" : "Catatan"}
              </label>
              <TextArea
                rows={2}
                value={type === "past" ? task.result_comment : task.note}
                onChange={(e) =>
                  updateTaskValue(
                    type,
                    index,
                    type === "past" ? "result_comment" : "note",
                    e.target.value
                  )
                }
              />
            </div>
            <div className="col-md-4">
              <label className="pa-field-label">Tahun</label>
              <Input value={task.year} disabled />
            </div>
            {type === "past" && (
              <div className="col-12">
                <label className="pa-field-label">
                  Evidence (PDF, maks 10MB)
                </label>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <a
                    href={I_LOVE_PDF_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="pa-evidence-helper-link ms-auto"
                  >
                    Butuh mengubah file ke PDF?
                  </a>
                </div>
                <div className="pa-task-evidence">
                  <Upload
                    showUploadList={false}
                    accept=".pdf"
                    beforeUpload={(file) => {
                      handleEvidenceUpload(type, index, file);
                      return false;
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={Boolean(
                        evidenceLoading[buildEvidenceKey(type, index)]
                      )}
                    >
                      Unggah Evidence
                    </Button>
                  </Upload>
                  {task.evidence_file_url ? (
                    <div className="pa-evidence-preview">
                      <a
                        href={task.evidence_file_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {task.evidence_file_name || "Lihat Evidence"}
                      </a>
                      {task.evidence_file_size ? (
                        <small className="text-muted ms-2">
                          ({formatFileSize(task.evidence_file_size)})
                        </small>
                      ) : null}
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => handleEvidenceRemove(type, index)}
                      >
                        Hapus
                      </Button>
                    </div>
                  ) : (
                    <span className="text-muted small">
                      Belum ada evidence.
                    </span>
                  )}
                </div>
              </div>
            )}
            {canComment &&
              isTaskChanged(task, originalTasksRef.current, index) && (
              <div className="col-12 pa-comment-field">
                <label className="pa-field-label">
                  Komentar Atasan Terkait <span className="text-danger">*</span>
                </label>
                <TextArea
                  rows={2}
                  placeholder="Catatan tambahan untuk tugas ini"
                  value={task.approver_comment || ""}
                  onChange={(e) =>
                    updateTaskValue(
                      type,
                      index,
                      "approver_comment",
                      e.target.value
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (loadingDetail || loadingMaster || !detail) {
      return (
        <div className="text-center my-5">
          <Spin size="large" />
        </div>
      );
    }

    return (
      <>
        <div className="pa-card">
          <p className="pa-section-title mb-1">Informasi Karyawan</p>
          <small className="text-muted">
            Data otomatis dari Talenta, hubungi HR jika ada ketidaksesuaian.
          </small>
          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <label className="pa-field-label">Nama Lengkap</label>
              <Input value={detail.fullname || "-"} disabled />
            </div>
            <div className="col-md-6">
              <label className="pa-field-label">Employee ID</label>
              <Input value={detail.employee_id || "-"} disabled />
            </div>
            <div className="col-md-6">
              <label className="pa-field-label">Jabatan</label>
              <Input value={detail.position || "-"} disabled />
            </div>
            <div className="col-md-6">
              <label className="pa-field-label">Level</label>
              <Input value={detail.level || "-"} disabled />
            </div>
            <div className="col-md-6">
              <label className="pa-field-label">Departemen</label>
              <Input value={detail.department || "-"} disabled />
            </div>
            <div className="col-md-3">
              <label className="pa-field-label">Periode Tahun</label>
              <Input value={detail.period_year || "-"} disabled />
            </div>
          </div>
        </div>

        <Divider orientation="left">Aspek Kinerja</Divider>
        {performanceMaster.length === 0 ? (
          <p className="text-center text-muted">
            Master performance belum tersedia untuk level ini.
          </p>
        ) : (
          performanceMaster.map((aspect) => (
            <div key={aspect.aspect_code} className="pa-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <p className="pa-section-title mb-0">
                    {aspect.aspect_label}
                  </p>
                  <small className="text-muted">{aspect.aspect_code}</small>
                </div>
                <span className="pa-aspect-order">
                  Urutan {aspect.order}
                </span>
              </div>
              <Radio.Group
                className="pa-performance-options"
                value={performanceValues[aspect.aspect_code]}
                onChange={(e) =>
                  handlePerformanceChange(aspect.aspect_code, e.target.value)
                }
              >
                {aspect.statements.map((statement) => (
                  <Radio
                    key={`${aspect.aspect_code}-${statement.order}`}
                    value={statement.order}
                    className="pa-performance-option"
                  >
                    <div className="pa-radio-label">
                      <span className="pa-radio-order">
                        {statement.order}.
                      </span>
                      <span>{statement.description}</span>
                    </div>
                  </Radio>
                ))}
              </Radio.Group>
              {canComment &&
                isPerformanceChanged(
                  aspect.aspect_code,
                  performanceValues,
                  originalPerformanceRef.current
                ) && (
                <div className="mt-3 pa-comment-field">
                  <label className="pa-field-label">
                    Komentar Atasan Terkait <span className="text-danger">*</span>
                  </label>
                  <TextArea
                    rows={2}
                    placeholder="Tambahkan catatan untuk aspek ini"
                    value={performanceComments[aspect.aspect_code] || ""}
                    onChange={(e) =>
                      handlePerformanceCommentChange(
                        aspect.aspect_code,
                        e.target.value
                      )
                    }
                  />
                </div>
              )}
            </div>
          ))
        )}

        <Divider orientation="left">Kompetensi</Divider>
        {competencyMaster.length === 0 ? (
          <p className="text-center text-muted">
            Master competency belum tersedia untuk level ini.
          </p>
        ) : (
          competencyMaster.map((comp) => (
            <div key={comp.competency_code} className="pa-card">
              <p className="pa-section-title mb-1">{comp.competency_label}</p>
              {comp.statements.map((statement) => {
                const key = `${comp.competency_code}_${statement.statement_no}`;
                return (
                  <div key={key} className="pa-competency-item">
                    <p className="mb-2 fw-medium">
                      {statement.statement_no}. {statement.description}
                    </p>
                    <Radio.Group
                      optionType="default"
                      size="small"
                      value={competencyValues[key]}
                      onChange={(e) =>
                        handleCompetencyChange(key, e.target.value)
                      }
                      className="pa-competency-options mt-3"
                    >
                      <Radio.Button value={1}>Tidak Setuju</Radio.Button>
                      <Radio.Button value={2}>Cukup</Radio.Button>
                      <Radio.Button value={3}>Setuju</Radio.Button>
                    </Radio.Group>
                    {canComment &&
                      isCompetencyChanged(
                        key,
                        competencyValues,
                        originalCompetencyRef.current
                      ) && (
                      <div className="mt-2 pa-comment-field">
                        <label className="pa-field-label">
                          Komentar Atasan Terkait <span className="text-danger">*</span>
                        </label>
                        <TextArea
                          rows={2}
                          placeholder="Catatan tambahan untuk pernyataan ini"
                          value={competencyComments[key] || ""}
                          onChange={(e) =>
                            handleCompetencyCommentChange(key, e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* All job level except Non Staff */}
        {!isNonStaff && (
          <>
            <Divider orientation="left">Tugas</Divider>
            {renderTaskSection("12 Bulan Terakhir", pastTasks, "past")}
            {renderTaskSection("12 Bulan Berikutnya", futureTasks, "future")}
          </>
        )}
      </>
    );
  };

  return (
    <Fragment>
      <Navbar
        title="Adjustment"
        back={true}
        subtitle="Performance Appraisal"
        navigate={`/pa/view/${recordId}`}
      />
      <div className="container white-back-cr" style={{ minHeight: 500 }}>
        <div className="container mt-3 p-3">{renderContent()}</div>
      </div>

      <Affix offsetBottom={0}>
        <div className="pa-submit-bar text-center">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            loading={submitting}
            disabled={loadingDetail || loadingMaster || !detail}
            onClick={handleSubmit}
            className="pa-submit-btn"
          >
            Simpan Perubahan
          </Button>
        </div>
      </Affix>
      <Footer />
    </Fragment>
  );
}

export default PATransactionEdit;
